// src/web/stores/workspace.ts

import type { Workspace } from '@shared/types'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

interface WorkspaceState {
	// Current selected workspace
	currentWorkspaceId: string | null
	currentWorkspace: Workspace | null

	// Actions
	setCurrentWorkspace: (workspace: Workspace) => void
	setCurrentWorkspaceId: (workspaceId: string) => void
	clearWorkspace: () => void

	// Helper to initialize workspace from user's workspaces list
	initializeWorkspace: (workspaces: Workspace[], defaultWorkspaceId?: string) => void
}

export const useWorkspaceStore = create<WorkspaceState>()(
	persist(
		(set, get) => ({
			currentWorkspaceId: null,
			currentWorkspace: null,

			setCurrentWorkspace: (workspace) =>
				set({
					currentWorkspaceId: workspace.id,
					currentWorkspace: workspace,
				}),

			setCurrentWorkspaceId: (workspaceId) =>
				set({
					currentWorkspaceId: workspaceId,
				}),

			clearWorkspace: () =>
				set({
					currentWorkspaceId: null,
					currentWorkspace: null,
				}),

			initializeWorkspace: (workspaces, defaultWorkspaceId) => {
				const state = get()

				// Filter to only workspaces with DAM access
				const damWorkspaces = workspaces.filter((ws) => ws.has_dam_access)

				if (damWorkspaces.length === 0) {
					return
				}

				// Priority order:
				// 1. Already selected workspace (if still valid and has DAM access)
				// 2. Default workspace ID from parameter
				// 3. First workspace with DAM access
				let selectedWorkspace: Workspace | undefined

				// Check if current selection is still valid
				if (state.currentWorkspaceId) {
					selectedWorkspace = damWorkspaces.find((ws) => ws.id === state.currentWorkspaceId)
				}

				// Try default workspace ID
				if (!selectedWorkspace && defaultWorkspaceId) {
					selectedWorkspace = damWorkspaces.find((ws) => ws.id === defaultWorkspaceId)
				}

				// Fall back to first workspace with DAM access
				if (!selectedWorkspace) {
					selectedWorkspace = damWorkspaces[0]
				}

				if (selectedWorkspace) {
					set({
						currentWorkspaceId: selectedWorkspace.id,
						currentWorkspace: selectedWorkspace,
					})
				}
			},
		}),
		{
			name: 'dam-workspace',
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				currentWorkspaceId: state.currentWorkspaceId,
			}),
		}
	)
)
